import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useIdleSignOut } from './idleSignOut';
import type { ProfileEdits, UserProfile } from './types';

interface AuthValue {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  saveProfile: (edits: ProfileEdits) => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Ensure `users/{uid}` exists.
 *
 * **Do not delete this.** It is load-bearing for every new account on the web.
 *
 * This used to say a `recordSignup` blocking function wrote the document server-side, and
 * that this copy was a stopgap for older accounts. There is no such function: the project
 * has 16 deployed functions and none is `recordSignup`, and Identity Platform has no
 * blocking triggers configured at all. Checked 2026-10-01.
 *
 * So nothing writes `users/{uid}` except a client. Some accounts have no profile document
 * — the Expo client never wrote one, and iOS only writes on a read-then-create that
 * evidently did not always complete.
 *
 * Merge, never overwrite: the phone clients own firstName/lastName once a user has edited
 * them, and a web sign-in must not reset that.
 */
async function ensureProfile(user: User): Promise<UserProfile | null> {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return snap.data() as UserProfile;
  }

  const [first = '', ...rest] = (user.displayName ?? '').trim().split(/\s+/);
  const created: UserProfile = {
    firstName: first,
    lastName: rest.join(' '),
    email: user.email ?? '',
  };
  await setDoc(ref, { ...created, updatedAt: serverTimestamp() }, { merge: true });
  return created;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (next) {
        try {
          setProfile(await ensureProfile(next));
        } catch {
          // A failed profile write must not block the session — the user is signed in
          // either way, and the notes query does not depend on this document.
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
  }, []);

  // Here rather than in App.tsx, so it covers every signed-in screen — including Upgrade and
  // the entitlement-unavailable retry, which render before the routes exist.
  useIdleSignOut(user?.uid, !loading);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      profile,
      loading,
      signInWithGoogle: async () => {
        await signInWithPopup(auth, new GoogleAuthProvider());
      },
      /**
       * Apple is configured on the project already — the provider is enabled with a client
       * id, and the Services ID's return URLs include this project's `__/auth/handler`,
       * which is where the popup lands whatever origin served the page.
       *
       * Two things about Apple worth knowing when reading a profile afterwards. The name
       * scope is only honoured on the *first* authorisation, so a user who signed in on the
       * phone before arrives here with no `displayName` and `ensureProfile` has nothing to
       * derive initials from. And Hide My Email yields a private relay address, so the email
       * on the account may not be one the person recognises.
       */
      signInWithApple: async () => {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        await signInWithPopup(auth, provider);
      },
      signInWithEmail: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password);
      },
      signOut: async () => {
        await fbSignOut(auth);
      },
      saveProfile: async (edits) => {
        if (!user) throw new Error('Not signed in');
        // Merge, always. This document also holds the iOS toast and voice-feedback
        // settings; a replacing write from here would wipe them.
        await setDoc(
          doc(db, 'users', user.uid),
          { ...edits, updatedAt: serverTimestamp() },
          { merge: true }
        );
        // Keep the context in step so the header initials update without a round trip.
        setProfile((current) => ({ ...current, ...edits }));
      },
    }),
    [user, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
